<template>
  <!-- Loading Spinner -->
  <LoadingSpinner v-if="showPageLoading" />
  
  <div 
    class="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50"
    v-if="show"
    @click="closeModal"
  >
    <div 
      class="bg-gradient-to-br from-gray-900/95 via-blue-900/95 to-purple-900/95 backdrop-blur-lg rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 border border-white/20"
      @click.stop
    >
      <!-- Header -->
      <div class="bg-gradient-to-r from-purple-600/20 to-blue-600/20 backdrop-blur-sm p-6 rounded-t-2xl border-b border-white/10">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold text-white">
            {{ showForgotPassword ? 'Reset Password' : (isSignInMode ? 'Welcome Back' : 'Join Rendery Consortium') }}
          </h2>
          <button 
            @click="closeModal"
            class="text-white/80 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p class="text-white/80 mt-2">
          {{ showForgotPassword ? 'Enter your email to receive reset instructions' : (isSignInMode ? 'Sign in to your account' : 'Create your account and start your journey') }}
        </p>
      </div>

      <!-- Mode Toggle (hidden for forgot password) -->
      <div v-if="!showForgotPassword" class="px-6 pt-6">
        <div class="flex bg-white/10 backdrop-blur-sm rounded-lg p-1 border border-white/20">
          <button
            @click="isSignInMode = false"
            :class="[
              'flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all',
              !isSignInMode 
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' 
                : 'text-white/70 hover:text-white hover:bg-white/10'
            ]"
          >
            Sign Up
          </button>
          <button
            @click="isSignInMode = true"
            :class="[
              'flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all',
              isSignInMode 
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' 
                : 'text-white/70 hover:text-white hover:bg-white/10'
            ]"
          >
            Sign In
          </button>
        </div>
      </div>

      <!-- Form -->
      <div class="p-6">
        <!-- Sign In/Sign Up Form (hidden when forgot password is shown) -->
        <form v-if="!showForgotPassword" @submit.prevent="handleSubmit">
          <!-- Email -->
          <div class="mb-4">
            <label for="email" class="block text-sm font-medium text-white/90 mb-2">
              Email Address
            </label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              class="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all text-white placeholder-white/50"
              :class="{ 'border-red-500 focus:ring-red-500 focus:border-red-500': errors.email }"
              placeholder="Enter your email"
              required
            />
            <p v-if="errors.email" class="text-red-400 text-sm mt-1">{{ errors.email }}</p>
          </div>

          <!-- Password -->
          <div class="mb-4">
            <label for="password" class="block text-sm font-medium text-white/90 mb-2">
              Password
            </label>
            <input
              id="password"
              v-model="form.password"
              type="password"
              @blur="validatePasswordOnBlur"
              class="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all text-white placeholder-white/50"
              :class="{ 'border-red-500 focus:ring-red-500 focus:border-red-500': errors.password }"
              placeholder="Enter your password"
              required
            />
            <p v-if="errors.password" class="text-red-400 text-sm mt-1">{{ errors.password }}</p>
            
            <!-- Forgot Password Link (only for sign in) -->
            <div v-if="isSignInMode" class="mt-2 text-right">
              <button
                type="button"
                @click="showForgotPassword = true"
                class="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          <!-- Confirm Password (only for sign up) -->
          <div v-if="!isSignInMode" class="mb-4">
            <label for="confirmPassword" class="block text-sm font-medium text-white/90 mb-2">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              v-model="form.confirmPassword"
              type="password"
              class="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all text-white placeholder-white/50"
              :class="{ 'border-red-500 focus:ring-red-500 focus:border-red-500': errors.confirmPassword }"
              placeholder="Confirm your password"
              required
            />
            <p v-if="errors.confirmPassword" class="text-red-400 text-sm mt-1">{{ errors.confirmPassword }}</p>
          </div>

          <!-- Access Code (only for sign up) -->
          <div v-if="!isSignInMode" class="mb-6">
            <label for="accessCode" class="block text-sm font-medium text-white/90 mb-2">
              Access Code
            </label>
            <input
              id="accessCode"
              v-model="form.accessCode"
              type="text"
              class="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all text-white placeholder-white/50"
              :class="{ 'border-red-500 focus:ring-red-500 focus:border-red-500': errors.accessCode }"
              placeholder="Enter your access code"
              required
            />
            <p v-if="errors.accessCode" class="text-red-400 text-sm mt-1">{{ errors.accessCode }}</p>
            <p class="text-white/60 text-xs mt-1">Required to create an account</p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
          >
            <span v-if="loading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isSignInMode ? 'Signing In...' : 'Creating Account...' }}
            </span>
            <span v-else>{{ isSignInMode ? 'Sign In' : 'Create Account' }}</span>
          </button>
        </form>

        <!-- Forgot Password Form (replaces the main form when shown) -->
        <form v-if="showForgotPassword" @submit.prevent="handleForgotPassword">
          <!-- Email for Password Reset -->
          <div class="mb-4">
            <label for="forgotPasswordEmail" class="block text-sm font-medium text-white/90 mb-2">
              Email Address
            </label>
            <input
              id="forgotPasswordEmail"
              v-model="forgotPasswordForm.email"
              type="email"
              class="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all text-white placeholder-white/50"
              :class="{ 'border-red-500 focus:ring-red-500 focus:border-red-500': forgotPasswordErrors.email }"
              placeholder="Enter your email address"
              required
            />
            <p v-if="forgotPasswordErrors.email" class="text-red-400 text-sm mt-1">{{ forgotPasswordErrors.email }}</p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="forgotPasswordLoading"
            class="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
          >
            <span v-if="forgotPasswordLoading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending Reset Instructions...
            </span>
            <span v-else>Send Reset Instructions</span>
          </button>

          <!-- Back to Sign In -->
          <div class="mt-6 text-center">
            <button
              type="button"
              @click="showForgotPassword = false"
              class="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
            >
              ← Back to Sign In
            </button>
          </div>
        </form>

        <!-- Success Message -->
        <div v-if="success" class="mt-6 p-4 bg-green-500/20 backdrop-blur-sm border border-green-500/30 text-green-300 rounded-lg">
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            {{ isSignInMode ? 'Welcome back! Redirecting...' : 'Access code validated! Let\'s complete your application next.' }}
          </div>
        </div>

        <!-- Forgot Password Success Message -->
        <div v-if="forgotPasswordSuccess" class="mt-6 p-4 bg-green-500/20 backdrop-blur-sm border border-green-500/30 text-green-300 rounded-lg">
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            {{ forgotPasswordMessage }}
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="generalError" class="mt-6 p-4 bg-red-500/20 backdrop-blur-sm border border-red-500/30 text-red-300 rounded-lg">
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {{ generalError }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from './LoadingSpinner.vue';
import { apiCall } from '../config/api.js';

export default {
  name: 'SignUpModal',
  components: {
    LoadingSpinner
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    initialMode: {
      type: String,
      default: 'signup',
      validator: (value) => ['signup', 'signin'].includes(value)
    }
  },
  emits: ['close'],
  watch: {
    initialMode(newMode) {
      this.isSignInMode = newMode === 'signin';
    },
    show(newValue) {
      if (newValue) {
        // Reset mode when modal is opened
        this.isSignInMode = this.initialMode === 'signin';
      }
    }
  },
  data() {
    return {
      isSignInMode: this.initialMode === 'signin',
      showForgotPassword: false,
      form: {
        email: '',
        password: '',
        confirmPassword: '',
        accessCode: ''
      },
      forgotPasswordForm: {
        email: ''
      },
      errors: {},
      forgotPasswordErrors: {},
      loading: false,
      forgotPasswordLoading: false,
      success: false,
      forgotPasswordSuccess: false,
      forgotPasswordMessage: '',
      generalError: '',
      showPageLoading: false,
      errorTimeout: null
    }
  },

  beforeUnmount() {
    // Clear timeout when component is destroyed
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  },

  methods: {
    closeModal() {
      this.$emit('close');
      this.resetForm();
    },
    resetForm() {
      // Clear any existing timeout
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
        this.errorTimeout = null;
      }
      
      this.isSignInMode = this.initialMode === 'signin';
      this.showForgotPassword = false;
      this.form = {
        email: '',
        password: '',
        confirmPassword: '',
        accessCode: ''
      };
      this.forgotPasswordForm = {
        email: ''
      };
      this.errors = {};
      this.forgotPasswordErrors = {};
      this.loading = false;
      this.forgotPasswordLoading = false;
      this.success = false;
      this.forgotPasswordSuccess = false;
      this.forgotPasswordMessage = '';
      this.generalError = '';
      this.showPageLoading = false;
    },
    validatePasswordOnBlur() {
      if (!this.isSignInMode && this.form.password && this.form.password.length < 8) {
        this.errors.password = 'Password must be at least 8 characters long';
      } else if (this.errors.password === 'Password must be at least 8 characters long') {
        this.errors.password = '';
      }
    },
    validateForm() {
      this.errors = {};
      
      if (!this.form.email || !this.isValidEmail(this.form.email)) {
        this.errors.email = 'Please enter a valid email address';
      }
      
      if (!this.form.password) {
        this.errors.password = 'Password is required';
      } else if (!this.isSignInMode && this.form.password.length < 8) {
        this.errors.password = 'Password must be at least 8 characters long';
      }
      
      // Only validate password confirmation and access code for sign up
      if (!this.isSignInMode) {
        if (!this.form.confirmPassword) {
          this.errors.confirmPassword = 'Please confirm your password';
        } else if (this.form.password !== this.form.confirmPassword) {
          this.errors.confirmPassword = 'Passwords do not match';
        }
        
        if (!this.form.accessCode || this.form.accessCode.trim() === '') {
          this.errors.accessCode = 'Access code is required';
        } else if (this.form.accessCode.trim() !== 'pioneer2025') {
          this.errors.accessCode = 'Invalid access code';
        }
      }
      
      return Object.keys(this.errors).length === 0;
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    validateForgotPasswordForm() {
      this.forgotPasswordErrors = {};
      
      if (!this.forgotPasswordForm.email || !this.isValidEmail(this.forgotPasswordForm.email)) {
        this.forgotPasswordErrors.email = 'Please enter a valid email address';
      }
      
      return Object.keys(this.forgotPasswordErrors).length === 0;
    },
    async handleForgotPassword() {
      if (!this.validateForgotPasswordForm()) {
        return;
      }
      
      this.forgotPasswordLoading = true;
      this.generalError = '';
      this.forgotPasswordSuccess = false;
      
      try {
        const { response, data } = await apiCall('auth/password-reset/request', {
          method: 'POST',
          body: JSON.stringify({ 
            identifier: this.forgotPasswordForm.email
          })
        });
        
        if (response.ok && data.success) {
          this.forgotPasswordSuccess = true;
          this.forgotPasswordMessage = data.message || 'We will send to you an e-mail if your username or e-mail is found';
          
          // Reset the form after successful submission
          setTimeout(() => {
            this.showForgotPassword = false;
            this.forgotPasswordForm.email = '';
            this.forgotPasswordSuccess = false;
            this.forgotPasswordMessage = '';
          }, 3000);
        } else {
          this.generalError = response.status === 429 ? 
          'A password recovery attempt was already made. Please try again in a few minutes.' : 
          'Failed to send reset instructions. Please try again.';
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Forgot password error:', error);
        }
        this.generalError = 'Network error. Please check your connection and try again.';
      } finally {
        this.forgotPasswordLoading = false;
        this.autoDestroyErrorMessage();
      }
    },
    autoDestroyErrorMessage() {
      // Clear any existing timeout
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }
      
      // Set new timeout to clear error message after 10 seconds
      this.errorTimeout = setTimeout(() => {
        this.generalError = '';
      }, 10000);
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }
      
      this.loading = true;
      this.generalError = '';
      
      try {
        if (this.isSignInMode) {
          await this.handleSignIn();
        } else {
          await this.handleSignUp();
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Form submission error:', error);
        }
        this.generalError = 'Network error. Please check your connection and try again.';
      } finally {
        this.loading = false;
      }
    },
    async handleSignIn() {
      const { response, data } = await apiCall('auth/login', {
        method: 'POST',
        body: JSON.stringify({ 
          email: this.form.email,
          password: this.form.password
        })
      });
      
      if (response.ok && data.success) {
        this.success = true;
        
        // Store authentication data
        this.$authActions.setAuthData(data.token, data.user);
        
        // Determine where to redirect based on user status
        setTimeout(() => {
          this.showPageLoading = true;
          setTimeout(() => {
            this.closeModal();
            this.redirectUserBasedOnStatus(data.user);
          }, 1000);
        }, 1500);
      } else {
        this.generalError = data.message || 'Sign in failed. Please check your credentials and try again.';
      }
    },
    async handleSignUp() {
      const { response, data } = await apiCall('users/register', {
        method: 'POST',
        body: JSON.stringify({
          email: this.form.email,
          password: this.form.password,
          confirmPassword: this.form.confirmPassword,
          accessCode: this.form.accessCode
        })
      });
      
      if (response.ok && data.success) {
        this.success = true;
        
        // Store authentication data
        this.$authActions.setAuthData(data.token, data.user);
        
        // Store temporary data for profile completion
        localStorage.setItem('tempUserId', data.user.id);
        localStorage.setItem('tempUserEmail', data.user.email);
        
        // Show page loading animation and redirect
        setTimeout(() => {
          this.showPageLoading = true;
          setTimeout(() => {
            this.closeModal();
            this.$router.push(`/creator-onboarding/${data.user.id}`);
          }, 1000);
        }, 1500);
      } else {
        this.generalError = data.message || 'Failed to create account. Please try again.';
      }
    },
    redirectUserBasedOnStatus(user) {
      // Development-only logging
      if (process.env.NODE_ENV === 'development') {
        console.log('Redirecting user based on status:', user);
        console.log('User email:', user.email);
        console.log('User email type:', typeof user.email);
        console.log('Is admin check:', user.email === 'nancy@rendery.org');
        console.log('Email comparison details:', {
          userEmail: user.email,
          adminEmail: 'nancy@rendery.org',
          areEqual: user.email === 'nancy@rendery.org',
          trimmedEqual: user.email?.trim() === 'nancy@rendery.org'
        });
      }
      
      // Check if user is admin - this should be the FIRST check and MUST bypass all other checks
      if (user.email === 'nancy@rendery.org') {
        if (process.env.NODE_ENV === 'development') {
          console.log('Admin detected - redirecting to admin backoffice');
          console.log('About to call this.$router.push("/admin-backoffice")');
        }
        this.$router.push('/admin-backoffice');
        if (process.env.NODE_ENV === 'development') {
          console.log('Router push called for admin redirect');
        }
        return;
      }
      
      // Check if user has completed onboarding (primary check: profileCompleted field)
      if (user.profileCompleted || (user.additionalQuestionsData && Object.keys(user.additionalQuestionsData).length > 0)) {
        // User has completed onboarding, redirect to registration complete page
        if (process.env.NODE_ENV === 'development') {
          console.log('Redirecting to registration complete');
        }
        this.$router.push('/registration-complete');
      } else if (user.firstName && user.lastName) {
        // User has basic profile but not onboarding, redirect to additional questions
        if (process.env.NODE_ENV === 'development') {
          console.log('Redirecting to creator onboarding (has basic profile)');
        }
        localStorage.setItem('tempUserId', user.id);
        localStorage.setItem('tempUserEmail', user.email);
        this.$router.push(`/creator-onboarding/${user.id}`);
      } else {
        // User needs to complete basic profile first
        if (process.env.NODE_ENV === 'development') {
          console.log('Redirecting to creator onboarding (needs basic profile)');
        }
        localStorage.setItem('tempUserId', user.id);
        localStorage.setItem('tempUserEmail', user.email);
        this.$router.push(`/creator-onboarding/${user.id}`);
      }
    }
  }
}
</script> 